import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import KeywordBox from "../../components/KeywordBox";
import ColorService from "../../../services/ColorService";
import StatsService from "../../../services/StatsGridService";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import CreateFrenchPuzzlesConfirmDialog from "../../pzl_common/board/CreateFrenchPuzzlesConfirmDialog";
import PuzzleCard from "../../pzl_common/board/PuzzleCard";
import {
    PUBLISHED_LIST_ORDER_DEFAULT,
    PUBLISHED_LIST_ORDER_OPTIONS,
    publishedListSort,
} from "../../pzl_common/board/publishedListOrder";
import {
    adminTagFilterAutocompleteSlotProps,
    adminTagFilterChipSx,
} from "../../pzl_common/board/adminTagFilterChipSx";
import AnythingService from "../../system/AnythingService";
import AdminGridBoardService from "./AdminGridBoardService";
import Logger from "../../../services/Logger";

const stepInfo = {
    0: {
        title: "Draft Stage",
        description: "This is where puzzle content is initially created and worked on. Items in this stage are not yet ready for review."
    },
    1: {
        title: "Ready to Review",
        description: "Puzzle content has been completed and is ready for review by editors. All necessary components should be in place."
    },
    2: {
        title: "Reviewed",
        description: "Content has been reviewed and approved by editors. Any necessary changes have been made and verified."
    },
    3: {
        title: "Published",
        description: "Puzzle content is now live and available to users. Content is in its final published state."
    },
    4: {

    }
};

// Map step number to state string
const getStateFromStep = (step) => {
    const stepToStateMap = {
        0: 'DRAFT',
        1: 'READY_TO_REVIEW',
        2: 'REVIEWED',
        3: 'PUBLISHED'
    };
    return stepToStateMap[step] || null;
};

export default function Step0SelectDraft({
    step,
    stepState,
    selectedGenreId = "all",
    selectedType = "all",
    selectedTags = [],
    availableTags = [],
    tagsLoading = false,
    selectedFree = null,
    selectedLevel = "all",
    selectedGridSize = "all",
    onTypeChange,
    onTagsChange,
    onLevelChange,
    onFreeChange,
    onGridSizeChange,
    initialKeyword,
    onKeywordChange,
    handlePuzzleClick
}) {
    const info = stepInfo[step] || { title: "Unknown step", description: "" };
    const { language } = useAdminLanguage();
    
    // Internal state for data fetching
    const [puzzleData, setPuzzleData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15
    });
    const [totalElements, setTotalElements] = useState(0);
    const [listOrder, setListOrder] = useState(PUBLISHED_LIST_ORDER_DEFAULT);
    const sort = useMemo(() => {
        if (step === 3 || stepState === "PUBLISHED") {
            return publishedListSort(listOrder);
        }
        return [
            { field: "updatedAt", sort: "desc" },
            { field: "createdAt", sort: "desc" },
        ];
    }, [step, stepState, listOrder]);
    const [overviewStats, setOverviewStats] = useState(null);
    const [creatingFrench, setCreatingFrench] = useState(false);
    const [frenchResult, setFrenchResult] = useState(null);
    const [frenchError, setFrenchError] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [keywordDebounced, setKeywordDebounced] = useState(() => (initialKeyword != null && initialKeyword !== "" ? String(initialKeyword) : ""));

    // Sync persisted keyword from parent when it changes (e.g. step change causes remount with new key, but keep in sync)
    const initialKeywordStr = initialKeyword != null ? String(initialKeyword) : "";
    useEffect(() => {
        setKeywordDebounced((prev) => (prev !== initialKeywordStr ? initialKeywordStr : prev));
    }, [initialKeywordStr]);

    const handleKeywordChange = (value) => {
        const v = value ?? "";
        setKeywordDebounced(v);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        onKeywordChange?.(v);
    };

    // Fetch puzzle data
    useEffect(() => {
        // Skip if stepState is invalid
        if (!stepState) {
            return;
        }

        const fetchPuzzleData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                Logger.log("Fetching puzzles for state:", stepState, "step:", step);
                
                await AdminGridBoardService.requestList(
                    paginationModel,
                    sort,
                    setPuzzleData,
                    setTotalElements,
                    keywordDebounced,
                    stepState,
                    selectedGenreId,
                    language,
                    selectedType === "all" ? null : selectedType,
                    selectedTags,
                    selectedFree,
                    selectedLevel,
                    selectedType === "WORD_SEARCH" && selectedGridSize !== "all" ? selectedGridSize : null
                );
            } catch (err) {
                Logger.error("Error fetching puzzle data:", err);
                setError("Failed to load puzzle data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchPuzzleData();
    }, [step, stepState, paginationModel, sort, keywordDebounced, selectedGenreId, language, selectedType, selectedTags, selectedFree, selectedLevel, selectedGridSize]);

    // Reset pagination when step, genre, type, tags, free, or language change
    useEffect(() => {
        setPaginationModel({
            page: 0,
            pageSize: 15
        });
        setPuzzleData([]);
        setTotalElements(0);
    }, [step, selectedGenreId, selectedType, selectedTags, selectedFree, selectedLevel, selectedGridSize, language, keywordDebounced]);

    // Fetch state count stats based on current step and selected type filter
    useEffect(() => {
        const state = getStateFromStep(step);
        if (!state) {
            return;
        }

        const fetchStateCountStats = async () => {
            try {
                // When "all types" don't send puzzleType; otherwise use selected type
                const puzzleType = selectedType === "all" ? null : selectedType;
                const stats = await StatsService.getStateCountStats(state, puzzleType);
                setOverviewStats(stats);
            } catch (err) {
                Logger.error("Error fetching state count stats:", err);
            }
        };

        fetchStateCountStats();
    }, [step, selectedType]);

    const handlePageChange = (_event, newPage) => {
        setPaginationModel(prev => ({
            ...prev,
            page: newPage - 1 // MUI Pagination uses 1-based indexing
        }));
    };

    const handleCreateFrenchPuzzlesClick = () => {
        setConfirmDialogOpen(true);
    };

    const handleConfirmCreateFrenchPuzzles = async () => {
        try {
            setCreatingFrench(true);
            setFrenchError(null);
            setFrenchResult(null);

            // Call grid-tpl API only (grid puzzles page)
            const gridResult = await AnythingService.createFrenchVersionsForGrid();
            setFrenchResult(gridResult);
        } catch (err) {
            Logger.error("Error creating French puzzles:", err);
            setFrenchError(err.message || "Failed to create French puzzles");
        } finally {
            setCreatingFrench(false);
        }
    };

    const totalElementsCount = totalElements ?? 0;
    const totalPages = totalElementsCount > 0 ? Math.ceil(totalElementsCount / paginationModel.pageSize) : 0;

    return (
        <Box sx={{ p: 0 }}>
            <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                {/* First Column: Title and Description */}
                <Box sx={{ flex: '1 1 50%' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {info.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: step === 1 ? 2 : 0 }}>
                        {info.description}
                    </Typography>
                    {step === 1 && (
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCreateFrenchPuzzlesClick}
                                disabled={creatingFrench || language === 'french'}
                                startIcon={creatingFrench ? <CircularProgress size={16} /> : null}
                                sx={{ mb: 2 }}
                            >
                                {creatingFrench ? "Creating French Puzzles..." : "Create French Puzzles"}
                            </Button>
                            {language === 'french' && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 1 }}>
                                    If you want to use this, select EN button.
                                </Typography>
                            )}
                            {frenchError && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {frenchError}
                                </Alert>
                            )}
                            {frenchResult && (
                                <Alert severity="success" sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                        French grid puzzles created
                                    </Typography>
                                    <Typography variant="body2" component="div" sx={{ pl: 0 }}>
                                        • Total: {frenchResult.totalCount ?? 0}<br />
                                        • Created: {frenchResult.createdCount ?? 0}<br />
                                        • Skipped: {frenchResult.skippedCount ?? 0}
                                        {frenchResult.errors && frenchResult.errors.length > 0 && (
                                            <>
                                                <br />• Errors: {frenchResult.errors.length}
                                                {frenchResult.errors.map((error, idx) => (
                                                    <Typography key={idx} variant="caption" component="div" sx={{ pl: 2, color: 'error.main' }}>
                                                        - {error}
                                                    </Typography>
                                                ))}
                                            </>
                                        )}
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Second Column: Published Information */}
                {overviewStats && (overviewStats.countByDifficulty || overviewStats.countByType) && (
                    <Box sx={{ flex: '1 1 50%', display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {overviewStats.countByDifficulty && (
                                <>
                                    <Box sx={{ 
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 1.5,
                                        minWidth: 60,
                                        textAlign: 'center',
                                        backgroundColor: ColorService.getStateColor('published')
                                    }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>
                                            Total
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: 'white' }}>
                                            {overviewStats.countByDifficulty.total}
                                        </Typography>
                                    </Box>
                                    {overviewStats.countByDifficulty.EASY > 0 && (
                                        <Box sx={{ 
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: 1.5,
                                            minWidth: 60,
                                            textAlign: 'center',
                                            backgroundColor: ColorService.getDifficultyColor('easy')
                                        }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>
                                                Easy
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: 'white' }}>
                                                {overviewStats.countByDifficulty.EASY}
                                            </Typography>
                                        </Box>
                                    )}
                                    {overviewStats.countByDifficulty.MEDIUM > 0 && (
                                        <Box sx={{ 
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: 1.5,
                                            minWidth: 60,
                                            textAlign: 'center',
                                            backgroundColor: ColorService.getDifficultyColor('medium')
                                        }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>
                                                Medium
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: 'white' }}>
                                                {overviewStats.countByDifficulty.MEDIUM}
                                            </Typography>
                                        </Box>
                                    )}
                                    {overviewStats.countByDifficulty.HARD > 0 && (
                                        <Box sx={{ 
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: 1.5,
                                            minWidth: 60,
                                            textAlign: 'center',
                                            backgroundColor: ColorService.getDifficultyColor('hard')
                                        }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>
                                                Hard
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: 'white' }}>
                                                {overviewStats.countByDifficulty.HARD}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                            {overviewStats.countByType && Object.entries(overviewStats.countByType)
                                .filter(([type]) => type !== 'CROSSWORD')
                                .map(([type, counts]) => {
                                const total = (counts.PAID ?? 0) + (counts.FREE ?? 0);
                                const showFreePaid = step === 3; // PUBLISHED only
                                const typeBgColor = String(type).startsWith("WORD_SEARCH") ? "#2196f3" : (type === "HIDDEN_MESSAGE" ? "#6366f1" : "#9e9e9e");
                                const labelWords = String(type).startsWith("WORD_SEARCH(")
                                    ? [type]
                                    : type.replace(/_/g, " ").split(" ");
                                return (
                                <Box key={type} sx={{ 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1.5,
                                    minWidth: showFreePaid ? 100 : 60,
                                    backgroundColor: typeBgColor,
                                    textAlign: 'center'
                                }}>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white', lineHeight: 1.2 }}>
                                        {labelWords.map((word, i) => (
                                            <Box key={i} component="span" sx={{ display: 'block' }}>{word}</Box>
                                        ))}
                                    </Typography>
                                    {showFreePaid ? (
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Box sx={{ 
                                                textAlign: 'center',
                                                backgroundColor: ColorService.getPricingColor(false),
                                                borderRadius: 1,
                                                p: 0.5,
                                                minWidth: 40
                                            }}>
                                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'white' }}>
                                                    PAID
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: 'white' }}>
                                                    {counts.PAID}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ 
                                                textAlign: 'center',
                                                backgroundColor: ColorService.getPricingColor(true),
                                                borderRadius: 1,
                                                p: 0.5,
                                                minWidth: 40
                                            }}>
                                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'white' }}>
                                                    FREE
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: 'white' }}>
                                                    {counts.FREE}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography variant="h6" sx={{ textAlign: 'center', color: 'white' }}>
                                            {total}
                                        </Typography>
                                    )}
                                </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Type, Size, Free/Paid, Level, Order, and Tags Filters - Always visible on every step */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter by Type</InputLabel>
                    <Select
                        value={selectedType}
                        label="Filter by Type"
                        onChange={(e) => onTypeChange && onTypeChange(e.target.value)}
                    >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="WORD_SEARCH">Word Search</MenuItem>
                        <MenuItem value="HIDDEN_MESSAGE">Hidden Message</MenuItem>
                    </Select>
                </FormControl>
                {selectedType === "WORD_SEARCH" && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Size</InputLabel>
                        <Select
                            value={selectedGridSize || "all"}
                            label="Size"
                            onChange={(e) => onGridSizeChange && onGridSizeChange(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="9">9×9</MenuItem>
                            <MenuItem value="12">12×12</MenuItem>
                        </Select>
                    </FormControl>
                )}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Level</InputLabel>
                    <Select
                        value={selectedLevel || "all"}
                        label="Level"
                        onChange={(e) => onLevelChange && onLevelChange(e.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="EASY">Easy</MenuItem>
                        <MenuItem value="MEDIUM">Medium</MenuItem>
                        <MenuItem value="HARD">Hard</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Free/Paid</InputLabel>
                    <Select
                        value={selectedFree === null ? "all" : selectedFree === true ? "free" : "paid"}
                        label="Free/Paid"
                        onChange={(e) => {
                            const value = e.target.value;
                            if (onFreeChange) {
                                if (value === "all") {
                                    onFreeChange(null);
                                } else if (value === "free") {
                                    onFreeChange(true);
                                } else {
                                    onFreeChange(false);
                                }
                            }
                        }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="free">Free</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                    </Select>
                </FormControl>
                {(step === 3 || stepState === "PUBLISHED") ? (
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Order</InputLabel>
                        <Select
                            value={listOrder}
                            label="Order"
                            onChange={(e) => {
                                setListOrder(e.target.value);
                                setPaginationModel((prev) => ({ ...prev, page: 0 }));
                            }}
                        >
                            {PUBLISHED_LIST_ORDER_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : null}
                <Autocomplete
                    multiple
                    size="small"
                    options={availableTags}
                    value={selectedTags.map((s) => availableTags.find((o) => (typeof o === "string" ? o : (o.tag ?? o.name ?? String(o))) === s) ?? s)}
                    isOptionEqualToValue={(opt, val) => (typeof opt === "string" ? opt : (opt.tag ?? opt.name ?? String(opt))) === (typeof val === "string" ? val : (val.tag ?? val.name ?? String(val)))}
                    onChange={(event, newValue) => {
                        const normalizedTags = newValue.map((tag) => (typeof tag === "string" ? tag : (tag.tag ?? tag.name ?? String(tag))));
                        onTagsChange && onTagsChange(normalizedTags);
                    }}
                    loading={tagsLoading}
                    slotProps={adminTagFilterAutocompleteSlotProps}
                    sx={{
                        minWidth: 200,
                        maxWidth: 260,
                        width: { xs: "100%", sm: 220 },
                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const tagLabel = typeof option === "string" ? option : (option.tag ?? option.name ?? String(option));
                            return (
                                <Chip
                                    variant="outlined"
                                    label={tagLabel}
                                    {...getTagProps({ index })}
                                    sx={adminTagFilterChipSx}
                                />
                            );
                        })
                    }
                    getOptionLabel={(option) => {
                        const name = typeof option === "string" ? option : (option.tag ?? option.name ?? String(option));
                        const count = typeof option === "object" && option != null ? option.count : undefined;
                        if (count != null && count !== "") return `${name} (${count})`;
                        return name;
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Filter by Tags"
                            placeholder="Select tags"
                        />
                    )}
                />
                <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 280 } }}>
                    <KeywordBox
                        fullWidth
                        label="Keyword"
                        placeholder="Search by keyword"
                        initialValue={initialKeyword}
                        callback={handleKeywordChange}
                    />
                </Box>
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 200
                    }}
                >
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading puzzles...
                    </Typography>
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : puzzleData.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 200
                    }}
                >
                    <Typography variant="body1">
                        No puzzles in this stage
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {puzzleData.length} {totalElementsCount > 0 ? `of ${totalElementsCount}` : ''} puzzles
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        {puzzleData.map((puzzle) => (
                            <Box 
                                key={puzzle.id} 
                                sx={{ 
                                    minWidth: '500px',
                                    maxWidth: '500px',
                                    flex: '0 0 auto'
                                }}
                            >
                                <PuzzleCard
                                    puzzle={puzzle}
                                    onClick={handlePuzzleClick}
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={paginationModel.page + 1}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Confirmation Dialog for Creating French Puzzles */}
            <CreateFrenchPuzzlesConfirmDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleConfirmCreateFrenchPuzzles}
            />
        </Box>
    );
}
